import { useState } from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import classNames from "classnames";
import { ReactComponent as LogoFilledFull } from "./Icons/LogoFilledFull.svg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import Link from "@mui/material/Link";
import { useSignup } from "./auth/useSignup";
import { showNotification } from "../redux/slices/notificationSlice";
import SocialBar from "./SocialBar";
import useEnhancedNavigate from "./Session/helpers";
import { formattedNotifMessage } from "../helpers";
import Footer from "./Plans/Footer";
import LoadingButton from "./Shared/LoadingButton";
import { renderPhoneField, renderTextField, validateForm } from "./auth/helpers";
import { closeAuthModal, openAuthModal } from "../redux/slices/userSlice";
import { AuthActions } from "./Sidebar/constants";
import { AlertMessageType } from "../assets/interfaces/interfaces";
import AlertMessage from "./ui/Alert";

const SignupPage = () => {
  const navigate = useEnhancedNavigate(); // Use the enhanced navigate function
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [nameError, setNameError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isEmailEntered, setIsEmailEntered] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessageType>(null);
  const dispatch: AppDispatch = useDispatch();

  const { authModal } = useSelector((state: { user }) => state.user);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
  };
  const handleLogoClick = () => {
    navigate("/");
  };

  const signup = useSignup();
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!isEmailEntered) {
      if (validateEmail(email)) {
        setIsEmailEntered(true);
        setEmailError("");
      }
      else {
        setEmailError("Please enter a valid email address.");
      }
      return;
    }
    setIsLoading(true);
    const { valid, errors } = validateForm({ email, name, phoneNumber, password });
    // Update error states
    setEmailError(errors.email);
    setNameError(errors.name);
    setPhoneNumberError(errors.phoneNumber);
    setPasswordError(errors.password);
    if (!valid) {
      setIsLoading(false);
      return;
    }
    try {
      await signup({ email, password, displayName: name, phoneNumber, landing: true });
      dispatch(showNotification({ message: "Sign Up Successful!", severity: "success", horizontal: "right" }));
      dispatch(closeAuthModal());
    }
    catch (error) {
      const formattedMessage = formattedNotifMessage(error.code);
      setAlertMessage({ message: formattedMessage, severity: "error" });
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleLogin = () => {
    if (authModal.type) {
      dispatch(openAuthModal({ type: AuthActions.SIGNIN }));
    }
    else {
      navigate("/signin");
    }
  };
  return (
    <div className="w-full h-full flex flex-col justify-between p-4 md:p-12 bg-white dark:bg-background/paper-elevation-1 text-mui-black-87 dark:text-white overflow-auto">
      <div className="flex items-center justify-center pb-6 cursor-pointer" onClick={handleLogoClick}>
        <LogoFilledFull className="text-black dark:text-white w-8 h-[34px]" />
        <h2 className="text-xl font-semibold ml-3 text-black dark:text-white">TradeGPT</h2>
      </div>

      <div className="flex-grow">
        <section className="flex justify-center items-center h-full">
          <Box className="!h-auto">
            <div className="flex flex-col justify-center items-center">
              <Typography variant="h4/regular-400" className="!text-[24px] md:!text-[34px]">
                Create your account
              </Typography>
            </div>
            <form onSubmit={handleSignup} className="pt-8 flex flex-col justify-start items-center gap-y-4 md:gap-y-6">
              {renderTextField("Email address", email, e => setEmail(e.target.value), emailError)}
              {isEmailEntered && (
                <>
                  {renderTextField("Name", name, e => setName(e.target.value), nameError)}
                  {renderPhoneField("Phone Number", phoneNumber, handlePhoneChange, phoneNumberError)}
                  {renderTextField("Password", password, e => setPassword(e.target.value), passwordError, "password")}
                  <div className="flex w-[300px] md:w-[340px] items-start gap-x-2 pt-2">
                    <Checkbox
                      className={
                        classNames("!p-0 text-mui-black-60 dark:text-mui-white-70",
                          {
                            "!text-tgpt-secondary-main": isTermsAccepted,
                          },
                        )
                      }
                      checked={isTermsAccepted}
                      onChange={e => setIsTermsAccepted(e.target.checked)}
                    />
                    <span className="text-sm font-semibold">
                      By creating an account, I agree to TradeGPT&apos;s
                      {" "}
                      {" "}
                      <Link className="!text-tgpt-secondary-main" href="/policies/service-terms" target="_blank" underline="none">
                        Terms of Service
                      </Link>
                      ,
                      {" "}
                      <Link className="!text-tgpt-secondary-main" href="/policies/privacy-policy" target="_blank" underline="none">
                        Privacy Policy
                      </Link>
                      , and
                      {" "}
                      <Link className="!text-tgpt-secondary-main" href="/policies/legal-disclaimer" target="_blank" underline="none">
                        Legal Disclaimer
                      </Link>
                      .
                    </span>
                  </div>
                </>
              )}
              {
                !isEmailEntered
                && (
                  <LoadingButton
                    variant="contained"
                    className="h-[50px] text-white text-[15px] font-medium leading-[26px] !bg-tgpt-secondary-main !normal-case disabled:!bg-action-disabled-background disabled:!text-action-disabled"
                    isDisabled={!email}
                    onClick={handleSignup}
                    isLoading={isLoading}
                    fullWidth
                    type="submit"
                  >
                    Continue
                  </LoadingButton>

                )
              }
              {alertMessage
              && (
                <div className="pt-2">
                  <AlertMessage message={alertMessage.message} severity={alertMessage.severity} />
                </div>
              )}
              {
                isEmailEntered
                && (
                  <LoadingButton
                    variant="contained"
                    isDisabled={!isTermsAccepted}
                    className="h-[50px] text-white !bg-tgpt-secondary-main !normal-case disabled:!bg-action-disabled-background disabled:!text-action-disabled"
                    onClick={handleSignup}
                    isLoading={isLoading}
                    fullWidth
                    type="submit"
                  >
                    Sign up
                  </LoadingButton>
                )
              }
              {/* Sign-up link */}
              <Typography variant="body1" className="text-center">
                Already have an account?
                {" "}
                <span onClick={handleLogin} className="cursor-pointer text-primary-blue-dark hover:underline">
                  Log in here
                </span>
              </Typography>
              {!isEmailEntered && (
                <SocialBar />
              )}
            </form>
          </Box>
        </section>
      </div>
      {
        !isEmailEntered && <Footer showLogo={false} />
      }
    </div>
  );
};

export default SignupPage;
